import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Params} from '@angular/router';
import {Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {Logger} from '../../class/helper/logger.class';
import {AuthState, IUser} from '../../models/auth';
import {Loader} from '../../models/loader/loader.redux';
import {
  IRecordEnvironmentAffectivity,
  RecordEnvironmentAffectivityAction,
  RecordEnvironmentAffectivityState
} from '../../models/record-environment-affectivity';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {StorageService} from '../../services/storage/storage.service';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {cloneDeep} from 'lodash';
import {ServiceAction, ServiceState} from '../../models/service';
import {IRecordEmit} from '../../interfaces/record';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {NzMarks} from 'ng-zorro-antd/slider';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-environment-affectivity',
  templateUrl: './record-history-environment-affectivity.component.html',
  styleUrls: ['./record-history-environment-affectivity.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordHistoryEnvironmentAffectivityComponent implements OnInit, OnDestroy {

  isPatient$: Observable<boolean> = this.store.select<boolean>(AuthState.getIsPatient);
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(RecordEnvironmentAffectivityState.getWasDeployed);

  @Input() recordEnvironmentAffectivity: IRecordEnvironmentAffectivity;
  @Output() recordEnvironmentAffectivityChange = new EventEmitter<IRecordEmit>();

  form: UntypedFormGroup;

  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;

  userLogged: IUser;
  disableForm: boolean;
  loadInfo: boolean;

  wasDeployedSubscription: Subscription;


  marks: NzMarks = {
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
    6: ''
  };
  skeletonTemplate: any[] = [];
  constructor(
    private store: Store,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private messageAlert: MessageAlertService,
    private skeletonService: SkeletonService,
  ) {
    this.disableForm = false;
    this.loadInfo = false;

    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordEnvironmentAffectivity) {
        this.loadInfo = false;
        this.initValuesHttp();
      }
    });
  }

  ngOnInit(): void {
    this.initValues();
  }

  initValues(): void {
    this.form = new UntypedFormGroup({});
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS)
  }

  async initValuesHttp(): Promise<void> {
    try {

      // await firstValueFrom(this.store.dispatch(new Loader(true, 'Cargando afectividad de entornos ...')));
      this.getParams();
      this.userLogged = cloneDeep(this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user);
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      if (!this.serviceId) {
        await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId)));
        this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      }
      await firstValueFrom(this.store.dispatch(new RecordEnvironmentAffectivityAction.GetActive(this.serviceId, this.patientId)));
      this.recordEnvironmentAffectivity = cloneDeep(
        await firstValueFrom(this.store.selectOnce(RecordEnvironmentAffectivityState.getRecordEnvironmentAffectivity))
      );
      this.recordEnvironmentAffectivityChange.emit({ record: this.recordEnvironmentAffectivity, updated: false });

      this.setFormProperties(this.recordEnvironmentAffectivity);
      this.loadInfo = true;
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  setFormProperties(recordEnvironmentAffectivity: IRecordEnvironmentAffectivity): void {
    this.disableForm = !this.userLogged?.experts;
    this.form = new UntypedFormGroup({
      someoneToTalkAboutMyProblems: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.someoneToTalkAboutMyProblems : 0, []),
      trustWithFamilyAndFriends: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.trustWithFamilyAndFriends : 0, []),
      leastOneWhoLovesMe: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.leastOneWhoLovesMe : 0, []),
      timeForHaveFun: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.timeForHaveFun : 0, []),
      enjoyFriendsAndFamilyTime: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.enjoyFriendsAndFamilyTime : 0, []),
      getWellWithPeople: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.getWellWithPeople : 0, []),
      tryDoThingMakeMeHappy: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.tryDoThingMakeMeHappy : 0, []),
      feelHappyOften: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.feelHappyOften : 0, []),
      enjoyLife: new UntypedFormControl(recordEnvironmentAffectivity ? recordEnvironmentAffectivity.enjoyLife : 0, []),
    });

    if (!this.userLogged.experts) {
      // tslint:disable-next-line:no-unused-expression
      this, this.form.disable();
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  emitChangeUpdate(): void {
    this.recordEnvironmentAffectivity.updatedAt = new Date().toISOString();
    this.recordEnvironmentAffectivityChange.emit({ record: this.recordEnvironmentAffectivity, updated: true });
  }

  async onSave(): Promise<void> {
    this.form.markAllAsTouched();
    this.form.markAsDirty();

    try {
      await firstValueFrom(this.store.dispatch(new Loader(true, 'Guardando historial ...')));
      await firstValueFrom(this.store.dispatch(new RecordEnvironmentAffectivityAction.PatchActive(
        this.serviceId, this.patientId, this.form.getRawValue())
      ));
      this.messageAlert.createMessage('success', 'Historial de afectividad y entorno guardado exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al actualizar el entorno de. Intente más tarde', 5000);
      this.recordEnvironmentAffectivity = cloneDeep(
        await firstValueFrom(this.store.selectOnce(RecordEnvironmentAffectivityState.getRecordEnvironmentAffectivity))
      );
      this.setFormProperties(this.recordEnvironmentAffectivity);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  sliderFormatter(value: number): string {
    let status = '';
    switch (value) {
      case 1:
        status = 'fuertemente en desacuerdo';
        break;
      case 2:
        status = 'moderadamente en desacuerdo';
        break;
      case 3:
        status = 'Medianamente en desacuerdo';
        break;
      case 4:
        status = 'Medianamente de acuerdo';
        break;
      case 5:
        status = 'Moderadamente de acuerdo';
        break;
      case 6:
        status = 'Fuertemente de acuerdo';
        break;
    }
    return status;
  }

  ngOnDestroy(): void {
    this.wasDeployedSubscription.unsubscribe();
  }
}
